/**
 * Frontend mirror of backend/lib/homepageSectionDefaults.js
 * Defaults for Why Choose / Fresh Kitchen / Testimonials copy.
 */

export type HomepageTestimonialItem = {
  name: string;
  rating: number;
  text: string;
  occasion: string;
};

export const DEFAULT_FEATURES_SECTION = {
  features_section_title: "Why Choose Nanny B's Bakery?",
  features_section_description:
    "We're passionate about creating delicious baked goods that bring joy to every occasion.",
  features_section_visible: true,
};

export const DEFAULT_KITCHEN_GALLERY = {
  kitchen_gallery_title: 'Fresh from Our Kitchen',
  kitchen_gallery_description:
    'Real photos of our latest creations — baked with love right here in Barrie.',
  kitchen_gallery_button_text: 'See More in Our Gallery →',
  kitchen_gallery_visible: true,
};

export const DEFAULT_TESTIMONIALS = {
  testimonials_title: 'What Our Customers Say',
  testimonials_description: "Don't just take our word for it - hear from our happy customers!",
  testimonials_visible: true,
  testimonials_items: [
    {
      name: 'Sarah M.',
      rating: 5,
      text: "The best bakery in Barrie! My daughter's birthday cake was absolutely stunning and delicious. Everyone asked where we got it!",
      occasion: 'Birthday Cake',
    },
    {
      name: 'Mike & Jennifer',
      rating: 5,
      text: "We ordered our wedding cake from Nanny B's and it exceeded all expectations. Beautiful, delicious, and Tammy was so easy to work with!",
      occasion: 'Wedding Cake',
    },
    {
      name: 'Lisa T.',
      rating: 5,
      text: "The stuffed cookies are INCREDIBLE! I order them every week now. My family can't get enough. Highly recommend!",
      occasion: 'Stuffed Cookies',
    },
    {
      name: 'David R.',
      rating: 5,
      text: "Fresh, homemade quality you can taste. The butter tarts remind me of my grandmother's. A true gem in Barrie!",
      occasion: 'Butter Tarts',
    },
  ] as HomepageTestimonialItem[],
};

/** "Want a Custom Cake?" quote block above pricing / call buttons */
export const DEFAULT_CAKE_QUOTE = {
  cake_quote_title: 'Want a Custom Cake?',
  cake_quote_description:
    'Every cake is one-of-a-kind. Pricing depends on size, design, flavour, and decorations — so Tammy loves to chat before quoting.',
  cake_quote_notice: '72 hours notice required on all custom cake orders.',
};

export function normalizeTestimonialsItems(raw: unknown): HomepageTestimonialItem[] {
  let items: unknown = raw;
  if (typeof items === 'string') {
    try {
      items = JSON.parse(items);
    } catch {
      return DEFAULT_TESTIMONIALS.testimonials_items;
    }
  }
  if (!Array.isArray(items) || items.length === 0) {
    return DEFAULT_TESTIMONIALS.testimonials_items;
  }
  const cleaned = items
    .map((item) => {
      const row = item as Partial<HomepageTestimonialItem>;
      return {
        name: String(row?.name || '').trim(),
        rating: Math.min(5, Math.max(1, Number(row?.rating) || 5)),
        text: String(row?.text || '').trim(),
        occasion: String(row?.occasion || '').trim(),
      };
    })
    .filter((item) => item.name && item.text);
  return cleaned.length > 0 ? cleaned : DEFAULT_TESTIMONIALS.testimonials_items;
}
